import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { downloadInvoice } from "../utils/invoice";

const STATUS_OPTIONS = [
  { value: "paid", label: "New paid order" },
  { value: "accepted", label: "Accepted" },
  { value: "pending-shipping", label: "Packed" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
];

const STATUS_BADGES = {
  paid: { bg: "#dcfce7", fg: "#166534", label: "New order" },
  accepted: { bg: "#dbeafe", fg: "#1d4ed8", label: "Accepted" },
  "pending-shipping": { bg: "#fef3c7", fg: "#92400e", label: "Packed" },
  shipped: { bg: "#e0e7ff", fg: "#3730a3", label: "Shipped" },
  delivered: { bg: "#def7ec", fg: "#03543f", label: "Delivered" },
};

const STEP_META = [
  { status: "paid", label: "Paid" },
  { status: "accepted", label: "Accepted" },
  { status: "pending-shipping", label: "Packed" },
  { status: "shipped", label: "Shipped" },
  { status: "delivered", label: "Delivered" },
];

const formatCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const resolveSellerId = (seller) => String(seller?._id || seller?.id || "");

const getFulfillment = (order, sellerId) =>
  (order.sellerFulfillments || []).find(
    (row) => String(row.sellerId?._id || row.sellerId || "") === String(sellerId)
  ) || null;

const getSellerItems = (order, sellerId) =>
  (order.items || []).filter(
    (row) => String(row.sellerId?._id || row.sellerId || "") === String(sellerId)
  );

const buildTimeline = (fulfillment) => {
  const historyMap = new Map((fulfillment?.statusHistory || []).map((entry) => [entry.status, entry]));
  const currentIndex = STEP_META.findIndex((step) => step.status === fulfillment?.status);

  return STEP_META.map((step, index) => {
    const history = historyMap.get(step.status);
    return {
      ...step,
      complete: currentIndex >= index,
      active: step.status === fulfillment?.status,
      at:
        history?.at ||
        (step.status === "pending-shipping" ? fulfillment?.packedAt : null) ||
        (step.status === "shipped" ? fulfillment?.shippedAt : null) ||
        (step.status === "delivered" ? fulfillment?.deliveredAt : null),
      note: history?.note || "",
    };
  });
};

const buildDraft = (order, sellerId) => {
  const fulfillment = getFulfillment(order, sellerId);
  return {
    status: fulfillment?.status || "paid",
    courierName: fulfillment?.courierName || "",
    trackingNumber: fulfillment?.trackingNumber || "",
    trackingUrl: fulfillment?.trackingUrl || "",
    estimatedDelivery: toDateInputValue(fulfillment?.estimatedDelivery),
    supportNote: fulfillment?.supportNote || "",
    statusNote: "",
  };
};

export default function SellerOrdersPanel({ seller }) {
  const sellerId = resolveSellerId(seller);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openOrderId, setOpenOrderId] = useState("");
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    if (!sellerId) return;
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  const loadOrders = async () => {
    setLoading(true);
    setMessage("");
    try {
      const response = await api.get("/api/sellers/orders");
      const rows = response.data?.orders || [];
      setOrders(rows);
      setOpenOrderId((prev) => prev || rows[0]?._id || "");
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to load seller orders");
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const fulfillment = getFulfillment(order, sellerId);
      if (!fulfillment) return false;
      if (statusFilter === "all") return true;
      if (statusFilter === "action") return ["paid", "accepted"].includes(fulfillment.status);
      if (statusFilter === "transit") return ["pending-shipping", "shipped"].includes(fulfillment.status);
      return fulfillment.status === statusFilter;
    });
  }, [orders, sellerId, statusFilter]);

  const summary = useMemo(() => {
    const rows = orders
      .map((order) => ({
        order,
        fulfillment: getFulfillment(order, sellerId),
        sellerItems: getSellerItems(order, sellerId),
      }))
      .filter((row) => row.fulfillment);

    return {
      total: rows.length,
      action: rows.filter((row) => ["paid", "accepted"].includes(row.fulfillment.status)).length,
      transit: rows.filter((row) => ["pending-shipping", "shipped"].includes(row.fulfillment.status)).length,
      delivered: rows.filter((row) => row.fulfillment.status === "delivered").length,
      revenue: rows.reduce(
        (sum, row) =>
          sum +
          row.sellerItems.reduce(
            (inner, item) => inner + Number(item.price || 0) * Number(item.qty || 1),
            0
          ),
        0
      ),
    };
  }, [orders, sellerId]);

  const updateDraft = (orderId, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || buildDraft(orders.find((row) => row._id === orderId) || {}, sellerId)),
        ...patch,
      },
    }));
  };

  const saveOrder = async (order) => {
    const draft = drafts[order._id] || buildDraft(order, sellerId);
    try {
      const response = await api.put(`/api/sellers/orders/${order._id}/fulfillment`, {
        ...draft,
        estimatedDelivery: draft.estimatedDelivery || null,
      });
      const updated = response.data?.order;
      if (updated) {
        setOrders((prev) => prev.map((row) => (row._id === updated._id ? updated : row)));
        setDrafts((prev) => ({ ...prev, [order._id]: buildDraft(updated, sellerId) }));
      }
      setMessage("Order shipment details updated.");
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to update shipment details");
    }
  };

  return (
    <section className="dashboard-panel" id="seller-orders">
      <div className="dashboard-heading">
        <div>
          <h3>Order Fulfillment</h3>
          <p>Accept orders, mark them packed, add courier details, and keep buyers informed clearly.</p>
        </div>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">All seller orders</option>
          <option value="action">Needs action</option>
          <option value="transit">In transit</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>

      <div style={metricGrid}>
        {[
          ["Seller orders", summary.total],
          ["Needs action", summary.action],
          ["In transit", summary.transit],
          ["Delivered", summary.delivered],
          ["Your sales", formatCurrency(summary.revenue)],
        ].map(([label, value]) => (
          <div key={label} style={metricCard}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      {message ? <div className="info-banner">{message}</div> : null}

      {loading ? (
        <p>Loading seller orders…</p>
      ) : filteredOrders.length === 0 ? (
        <p>No seller orders found for this filter yet.</p>
      ) : (
        <div className="seller-product-list">
          {filteredOrders.map((order) => {
            const fulfillment = getFulfillment(order, sellerId);
            const draft = drafts[order._id] || buildDraft(order, sellerId);
            const sellerItems = getSellerItems(order, sellerId);
            const badge = STATUS_BADGES[fulfillment?.status] || STATUS_BADGES.paid;
            const timeline = buildTimeline(fulfillment);
            const total = sellerItems.reduce(
              (sum, item) => sum + Number(item.price || 0) * Number(item.qty || 1),
              0
            );
            const customerPhone = String(order.shipping?.phone || order.customer?.phone || "").replace(/\D/g, "");
            const customerMessage = [
              `Hello ${order.customer?.name || "customer"},`,
              `This is an update for your order ${order.invoiceNumber || order.razorpay_order_id || order._id}.`,
              `Current status: ${badge.label}.`,
              draft.trackingNumber ? `Tracking number: ${draft.trackingNumber}` : "",
            ]
              .filter(Boolean)
              .join("\n");

            return (
              <article key={order._id} className="seller-product-card" style={orderCard}>
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ ...badgePill, background: badge.bg, color: badge.fg }}>{badge.label}</span>
                        <strong>{order.invoiceNumber || order.razorpay_order_id || order._id}</strong>
                      </div>
                      <p style={{ margin: "8px 0 0" }}>
                        {order.customer?.name || "Customer"} • {order.shipping?.city || order.customer?.city || "Delivery"} • {formatDate(order.createdAt)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenOrderId((prev) => (prev === order._id ? "" : order._id))}
                    >
                      {openOrderId === order._id ? "Hide order" : "Manage order"}
                    </button>
                  </div>

                  <div style={{ display: "grid", gap: 6 }}>
                    <p style={{ margin: 0, fontWeight: 700 }}>Your items</p>
                    {sellerItems.map((row, index) => (
                      <div key={`${order._id}-${index}`} style={{ color: "#475569" }}>
                        {(row.item?.name || "Product")} • Qty {row.qty} • {formatCurrency(Number(row.price || 0) * Number(row.qty || 1))}
                      </div>
                    ))}
                    <p style={{ margin: "6px 0 0", fontWeight: 700 }}>Your total: {formatCurrency(total)}</p>
                  </div>
                </div>

                {openOrderId === order._id ? (
                  <div style={detailWrap}>
                    <div style={detailPanel}>
                      <strong>Fulfillment timeline</strong>
                      <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                        {timeline.map((step) => (
                          <div key={`${order._id}-${step.status}`} style={{ display: "grid", gridTemplateColumns: "18px 1fr", gap: 10 }}>
                            <span
                              style={{
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                background: step.complete ? "#0f766e" : "#cbd5e1",
                                marginTop: 4,
                              }}
                            />
                            <div>
                              <div style={{ fontWeight: 700 }}>{step.label}</div>
                              <div style={{ color: "#64748b", fontSize: 13 }}>
                                {step.at ? formatDate(step.at) : "Waiting"}
                              </div>
                              {step.note ? <div style={{ color: "#334155", fontSize: 13, marginTop: 4 }}>{step.note}</div> : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={detailPanel}>
                      <strong>Update shipment</strong>
                      <div style={editorGrid}>
                        <select value={draft.status} onChange={(event) => updateDraft(order._id, { status: event.target.value })}>
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <input
                          placeholder="Courier name"
                          value={draft.courierName}
                          onChange={(event) => updateDraft(order._id, { courierName: event.target.value })}
                        />
                        <input
                          placeholder="Tracking number"
                          value={draft.trackingNumber}
                          onChange={(event) => updateDraft(order._id, { trackingNumber: event.target.value })}
                        />
                        <input
                          placeholder="Tracking link"
                          value={draft.trackingUrl}
                          onChange={(event) => updateDraft(order._id, { trackingUrl: event.target.value })}
                        />
                        <input
                          type="date"
                          value={draft.estimatedDelivery}
                          onChange={(event) => updateDraft(order._id, { estimatedDelivery: event.target.value })}
                        />
                        <input
                          placeholder="Customer status note"
                          value={draft.statusNote}
                          onChange={(event) => updateDraft(order._id, { statusNote: event.target.value })}
                        />
                        <textarea
                          placeholder="Support note for customer"
                          value={draft.supportNote}
                          onChange={(event) => updateDraft(order._id, { supportNote: event.target.value })}
                          style={{ minHeight: 90, gridColumn: "1 / -1" }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
                        <button type="button" onClick={() => saveOrder(order)}>
                          Save shipment update
                        </button>
                        <button type="button" className="secondary-button" onClick={() => downloadInvoice(order._id)}>
                          Seller invoice
                        </button>
                        {customerPhone ? (
                          <a
                            href={`https://wa.me/${customerPhone}?text=${encodeURIComponent(customerMessage)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="secondary-button"
                            style={linkButton}
                          >
                            WhatsApp customer
                          </a>
                        ) : null}
                      </div>
                    </div>

                    <div style={{ ...detailPanel, gridColumn: "1 / -1" }}>
                      <strong>Delivery details</strong>
                      <p style={{ margin: "10px 0 0" }}>
                        {order.shipping?.name || order.customer?.name || "Customer"}
                        {order.shipping?.phone ? ` • ${order.shipping.phone}` : ""}
                      </p>
                      <p style={{ margin: "8px 0 0", color: "#475569" }}>
                        {[order.shipping?.line1, order.shipping?.line2, order.shipping?.city, order.shipping?.state, order.shipping?.pincode]
                          .filter(Boolean)
                          .join(", ") || "Pickup order"}
                      </p>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

const metricGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 12,
  marginBottom: 16,
};

const metricCard = {
  display: "grid",
  gap: 6,
  border: "1px solid #dbeafe",
  borderRadius: 16,
  background: "#fff",
  padding: 14,
  color: "#0f172a",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const orderCard = {
  gridTemplateColumns: "1fr",
};

const badgePill = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 10px",
  borderRadius: 999,
  fontWeight: 800,
  fontSize: 12,
};

const detailWrap = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 14,
  marginTop: 18,
};

const detailPanel = {
  border: "1px solid #dbeafe",
  borderRadius: 16,
  background: "#f8fbff",
  padding: 16,
};

const editorGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  marginTop: 12,
};

const linkButton = {
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
